// ============================================================
// useDefenceSync - Firestore sync for pending defence prompts.
//
// Subscribes to the defence/ subcollection and provides
// helpers to create, clear, and query pending defences.
// Includes orphan cleanup (tab removed) and timeout cleanup.
//
// Follows useTargetSync architecture: DI via mp, echo
// prevention, useEffect listener.
// ============================================================

import { useState, useEffect, useRef, useCallback } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import useEchoPrevention, { type EchoPrevention } from './useEchoPrevention';
import useMirror from './useMirror';
import type { Tab } from '../types';
import type * as mpModule from '../multiplayer';
import { NO_VEIL, veilDefence, type Veil } from '../engine/roster/hidden';
import {
  clearPrompt,
  clearPromptsSince,
  createPrompt,
  startDefenceSync,
  type DefenceDoc,
  type SyncHandles,
} from './defence/sync';

interface UseDefenceSyncParams {
  sessionId: string | null;
  playerId: string | null;
  /** Current tab state (for orphan cleanup) */
  tabs: Tab[];
  /** Multiplayer service (DI) */
  mp: typeof mpModule;
  /**
   * Called when a reaction runs out of time, so the caller can resolve it as
   * "no reaction" and apply the damage. Without one the document is merely
   * deleted, which is the old behaviour and loses the attack.
   */
  onExpire?: (combatantId: string, doc: DefenceDoc) => void;
  /**
   * THE PROMPT'S ONE BOUNDARY (plan map-display, G1): a defence document is
   * veiled as it is written - the victim's player reads it - and names the
   * alias when a hidden figure fired. A ref, read at write time. Absent: nothing is veiled.
   */
  veil?: MutableRefObject<Veil>;
}

export interface UseDefenceSyncReturn {
  pendingDefences: Map<string, DefenceDoc>;
  createPendingDefence: (tabId: string, data: DefenceDoc) => void;
  clearPendingDefence: (tabId: string) => void;
  clearAllPendingDefences: () => void;
  /** Cancel only the reactions created after a history snapshot's watermark. */
  clearPendingDefencesSince: (watermark: number | null | undefined) => void;
  getDefenceForTab: (tabId: string) => DefenceDoc | null;
  isDefencePending: (tabId: string) => boolean;
}

type SetPending = Dispatch<SetStateAction<Map<string, DefenceDoc>>>;
type Mp = typeof mpModule;

/**
 * THE MAP IS LOCAL FIRST, AND A SESSION ONLY ADDS A NETWORK (v8.154.0).
 *
 * Both of these used to return early without a session, so solo play had
 * nowhere to put a reaction except the single `pendingAttack`. That was
 * fine while an attack owed one - and it silently ATE the rest the moment a
 * blast owed several: a victim who could dodge got neither the prompt (only
 * the aimed one gets `pendingAttack`) nor the damage (the code had already
 * taken the reaction branch). Their hits vanished without a line in the log.
 *
 * Keeping the map whether or not there is a session gives solo the same
 * queue multiplayer has, and costs nothing: the writes below are the only
 * part that ever needed a session.
 */
function useDefenceWrites(
  { sessionId, mp, veil }: Pick<UseDefenceSyncParams, 'sessionId' | 'mp' | 'veil'>,
  echo: EchoPrevention,
  setPendingDefences: SetPending,
) {
  const createPendingDefence = useCallback(
    (tabId: string, data: DefenceDoc) => {
      const v = veil?.current ?? NO_VEIL;
      // A hidden victim's prompt is the GM's alone: kept in the local map, never written (defence/sync keepLocalOnly).
      if (v.hides(tabId))
        return createPrompt({ sessionId: null, mp, echo, setPendingDefences }, tabId, { ...data, localOnly: true });
      return createPrompt({ sessionId, mp, echo, setPendingDefences }, tabId, veilDefence(v, data));
    },
    [sessionId, mp, echo, setPendingDefences, veil],
  );
  const clearDefence = useCallback(
    (tabId: string) => clearPrompt({ sessionId, mp, echo, setPendingDefences }, tabId),
    [sessionId, mp, echo, setPendingDefences],
  );
  return { createPendingDefence, clearDefence };
}

/**
 * CLEAR ONLY THE REACTIONS THE UNDONE ACTION CREATED.
 *
 * Undo used to call `clearAllPendingDefences`, which cancelled every
 * outstanding reaction in the session. That was harmless while an attack
 * could owe only one - and wrong the moment two are outstanding, because
 * rewinding my shot at Marius would also cancel Otto's answer to somebody
 * else's, and that attack then expires into damage nobody chose.
 *
 * The discriminator is the watermark every history snapshot already
 * carries: a reaction created after the action began belongs to it. This is
 * the same test the undo already applies to shared narrative entries
 * (`deleteNarrativeAfter`). A snapshot without one - written by an older
 * client - falls back to clearing everything, which is what it used to do.
 */
function useUndoClears(
  sessionId: string | null,
  mp: Mp,
  echo: EchoPrevention,
  pendingRef: MutableRefObject<Map<string, DefenceDoc>>,
  setPendingDefences: SetPending,
) {
  const clearAllDefences = useCallback(() => {
    if (!sessionId) return;
    setPendingDefences(new Map());
    mp.clearAllPendingDefences(sessionId).catch((err) => console.error('[Defence] clearAll error:', err));
  }, [sessionId, mp, setPendingDefences]);
  const clearDefencesSince = useCallback(
    (watermark: number | null | undefined) =>
      clearPromptsSince({ sessionId, mp, echo, setPendingDefences }, pendingRef, watermark, clearAllDefences),
    // clearAllDefences changes only with sessionId and mp, already listed.
    [sessionId, mp, echo, pendingRef, setPendingDefences, clearAllDefences],
  );
  return { clearDefencesSince, clearAllDefences };
}

/** The combatants this client knows about, for the listener's orphan check. */
function useTabIds(tabs: Tab[]): MutableRefObject<Set<string>> {
  const ref = useRef<Set<string>>(new Set());
  useEffect(() => {
    ref.current = new Set(tabs.map((t) => t.id));
  }, [tabs]);
  return ref;
}

/**
 * The Firestore listener and the expiry sweep (see `defence/sync`),
 * subscribed once per session. `atSubscribe` is the map as it stood then:
 * an echoed document keeps that entry.
 */
function useDefenceListener(
  sessionId: string | null,
  handles: Omit<SyncHandles, 'sessionId'>,
  atSubscribe: Map<string, DefenceDoc>,
) {
  useEffect(() => {
    if (!sessionId) {
      handles.setPendingDefences(new Map());
      return;
    }
    return startDefenceSync({ ...handles, sessionId }, atSubscribe);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- subscribe once per session; the refs carry what changes
  }, [sessionId]);
}

export default function useDefenceSync({
  sessionId,
  tabs,
  mp,
  onExpire,
  veil,
}: UseDefenceSyncParams): UseDefenceSyncReturn {
  // `playerId` is accepted and unread - reserved for future per-player defence filtering.
  const [pendingDefences, setPendingDefences] = useState<Map<string, DefenceDoc>>(new Map());
  const echo = useEchoPrevention();
  const tabIdsRef = useTabIds(tabs);
  // The sweep reads this rather than the state, because deciding what has
  // expired inside a state updater would make the updater impure - and React
  // invokes updaters more than once.
  const pendingRef = useMirror(pendingDefences);
  const onExpireRef = useMirror(onExpire);

  const { createPendingDefence, clearDefence } = useDefenceWrites({ sessionId, mp, veil }, echo, setPendingDefences);
  const { clearDefencesSince, clearAllDefences } = useUndoClears(sessionId, mp, echo, pendingRef, setPendingDefences);
  const getDefenceForTab = useCallback((tabId: string) => pendingDefences.get(tabId) || null, [pendingDefences]);
  const isDefencePending = useCallback((tabId: string) => pendingDefences.has(tabId), [pendingDefences]);

  useDefenceListener(sessionId, { mp, echo, tabIdsRef, pendingRef, onExpireRef, setPendingDefences }, pendingDefences);

  return {
    pendingDefences,
    createPendingDefence,
    clearPendingDefence: clearDefence,
    clearAllPendingDefences: clearAllDefences,
    clearPendingDefencesSince: clearDefencesSince,
    getDefenceForTab,
    isDefencePending,
  };
}
